import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Inject, Input, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { IconsModule, SVGIcon, SVGIconComponent } from '@progress/kendo-angular-icons';
import { Align, Collision, PopupModule } from '@progress/kendo-angular-popup';
import { filterIcon } from '@progress/kendo-svg-icons';

@Component({
    selector: 'grid-filter-popup',
    template: ` @if (isOpened) {
            <kendo-popup
                (click)="popupClicked($event)"
                [anchor]="popupAnchor.element"
                (anchorViewportLeave)="isOpened = false"
                [style.width]="width + 'px'"
                (open)="onPopupOpen()"
                (close)="onPopupClose()"
                [anchorAlign]="anchorAlign"
                [popupAlign]="popupAlign"
                [collision]="collision">
                <ng-content></ng-content>
                <div class="k-action-buttons">
                    <button
                        (click)="onResetClicked($event)"
                        color="cancel"
                        data-qa-id="NumberRangeFilterClear"
                        mat-flat-button
                        type="reset"
                        [disabled]="disableClear"
                        >Clear</button
                    >
                    <button
                        (click)="onFilterClicked($event)"
                        data-qa-id="NumberRangeFilterFilter"
                        mat-flat-button
                        color="primary"
                        [disabled]="!isFilterActive"
                        >Filter</button
                    >
                </div>
            </kendo-popup>
        }
        <div
            class="k-grid-filter"
            [class.k-state-active]="isFilterActive">
            <kendo-svgicon
                #popupAnchor
                (click)="filterToggled()"
                [icon]="filterIcon"
                [size]="'small'"></kendo-svgicon>
        </div>`,
    styles: [
        `
            kendo-popup ::ng-deep {
                z-index: 3 !important;
                .k-popup {
                    padding: 7px;
                }
            }

            .k-action-buttons {
                padding-bottom: 0;
            }

            mat-checkbox.mat-mdc-checkbox {
                padding-top: 10px;
                padding-left: 10px;
            }
        `
    ],
    standalone: true,
    imports: [IconsModule, PopupModule, MatCheckboxModule, MatButtonModule]
})
export class GridFilterPopupComponent {
    filterIcon: SVGIcon = filterIcon;
    isOpened = false;
    anchorAlign: Align = { horizontal: 'right', vertical: 'bottom' };
    popupAlign: Align = { horizontal: 'right', vertical: 'top' };
    collision: Collision = { horizontal: 'fit', vertical: 'fit' };

    @Input() width = 200;
    @Input() isFilterActive = false;
    @Input() disableClear = false;
    @Input() closePredicate: (event: any) => boolean;
    @Output() onResetClick = new EventEmitter<any>();
    @Output() onFilterClick = new EventEmitter<any>();
    @Output() onFilterToggle = new EventEmitter<boolean>();
    @Output() onOpen = new EventEmitter();
    @Output() onClose = new EventEmitter();

    @ViewChild('popupAnchor', { static: true }) public popupAnchor: SVGIconComponent;
    @ViewChild('popup', { read: ElementRef }) public popup: ElementRef;

    @HostListener('keydown', ['$event'])
    public keydown(event: KeyboardEvent): void {
        if (event.keyCode === 27) {
            this.close();
        }
    }

    @HostListener('document:click', ['$event'])
    public documentClick(event: any): void {
        if (this.closePredicate && this.closePredicate(event)) {
            return;
        }

        if (
            !this.contains(event.target) &&
            !event.target.closest('kendo-calendar') &&
            !this._document.querySelector('kendo-calendar')
        ) {
            this.close();
        }
    }

    constructor(@Inject(DOCUMENT) private readonly _document: Document) {}

    filterToggled(): void {
        this.isOpened = !this.isOpened;

        this.onFilterToggle.emit(this.isOpened);
    }

    onResetClicked($event): void {
        this.close();
        this.onResetClick.emit($event);
    }

    onFilterClicked($event): void {
        this.close();
        this.onFilterClick.emit($event);
    }

    close(): void {
        this.isOpened = false;
    }

    onPopupOpen(): void {
        this.onOpen.emit();
    }

    onPopupClose(): void {
        this.onClose.emit();
    }

    //if the click was in popup, stop propagation to prevent popup closing
    popupClicked($event): void {
        $event.stopPropagation();
    }

    private contains(target: any): boolean {
        return (
            this.popupAnchor.element.nativeElement.contains(target) ||
            (this.popup ? this.popup.nativeElement.contains(target) : false)
        );
    }
}
